import type { ReactNode } from 'react';

export type MetricTone = 'default' | 'warning' | 'critical' | 'positive';

const TONE_VALUE: Record<MetricTone, string> = {
  default: 'text-gray-900',
  warning: 'text-amber-600',
  critical: 'text-red-600',
  positive: 'text-green-700',
};

interface MetricTileProps {
  label: string;
  value: string;
  sub?: string;
  tone?: MetricTone;
  onClick?: () => void;
  icon?: ReactNode;
}

/**
 * Dashboard metric tile. Clickable tiles are the drill entry points —
 * rendered as a button so the whole surface is the hit target.
 */
export default function MetricTile({ label, value, sub, tone = 'default', onClick, icon }: MetricTileProps) {
  const content = (
    <>
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={`mt-2 text-2xl font-semibold tabular-nums ${TONE_VALUE[tone]}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-400 truncate">{sub}</div>}
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
      >
        {content}
      </button>
    );
  }

  return <div className="bg-white border border-gray-200 rounded-lg p-4">{content}</div>;
}
